import { Spin } from 'antd';
import { useEffect, useMemo, useState } from 'react';

import { useLocation, useNavigate } from '@suntex.core/nav';
import { $$ } from '@suntex.core/text';
import { removePrefix } from '@suntex.core/text/transforms';
import { $GradeLevel, GradeLevel } from '@suntex.fim/enums';
import { useModuleTagging, useModules } from '@suntex.fim/framework';
import {
  CanvasHub,
  HubSettings,
  ModuleFilter,
  buildModuleEnabledPredicate,
} from '@suntex.fim/hub';

import { usePlayerContext } from '../context';
import { usePlayerProgressSummary } from '../hooks';
import { useModuleNavigation } from '../navigation';
import { normalizeHubPath } from '../utils/modulePath';

import { ModuleCatalog } from './ModuleCatalog';

export interface HubProps {
  enable: ModuleFilter;
  disable: ModuleFilter;
  focus: ModuleFilter;
  gradeLevel: GradeLevel | undefined;
  settings: HubSettings;
}

export function Hub({
  enable,
  disable,
  focus,
  gradeLevel,
  settings,
}: HubProps) {
  const { hash } = useLocation();
  const navigate = useNavigate();
  const { navigateToModule } = useModuleNavigation();
  // Remove leading # from hash to normalize the path value
  const normalizedHash = normalizeHubPath($$(hash, removePrefix('#')));
  const [path, setPath] = useState(
    normalizedHash || settings.defaultPath || '/',
  );
  const modules = useModules();
  const { moduleTagging, isLoading: isTaggingLoading } = useModuleTagging();
  const { isPreview } = usePlayerContext();
  const progressSummary = usePlayerProgressSummary();

  const enabledModules = useMemo(() => {
    if (!modules || !moduleTagging) return modules;
    const isEnabled = buildModuleEnabledPredicate({
      moduleTagging,
      enable,
      disable,
    });
    return modules.filter((m) => isEnabled(m.slug));
  }, [modules, moduleTagging, enable, disable]);

  // Sync path state to URL hash when path changes
  useEffect(() => {
    const hashPath = $$(hash, removePrefix('#'));
    if (hashPath !== path) {
      navigate(`#${path}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, navigate]);

  // Sync URL hash to path state when hash changes externally
  useEffect(() => {
    const hashPath = normalizeHubPath($$(hash, removePrefix('#')));
    if (hashPath && hashPath !== path) {
      setPath(hashPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash]);

  useEffect(() => {
    if (gradeLevel) {
      const newPath = `/stages-of-learning/${$GradeLevel.describe(gradeLevel).slug}`;
      if (
        path.startsWith('/stages-of-learning/') &&
        !path.startsWith(newPath)
      ) {
        setPath(newPath);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gradeLevel]);

  if (settings.catalogView) {
    if (!modules || isTaggingLoading || !moduleTagging) return <Spin />;
    return (
      <ModuleCatalog
        modules={enabledModules!}
        path={path}
        moduleTagging={moduleTagging}
      />
    );
  } else {
    if (isTaggingLoading || !moduleTagging) return <Spin />;
    return (
      <>
        <span style={{ display: 'none' }}>
          {JSON.stringify({ hash, path })}
        </span>
        <CanvasHub
          config={{
            preview: isPreview,
          }}
          {...{
            // deliberately bypassing react-tracked hash
            path,
            enable,
            disable,
            focus,
            gradeLevel,
            progressSummary,
            settings,
            moduleTagging,
            setPath,
            navigate: navigateToModule,
          }}
        />
      </>
    );
  }
}
